/**
 * Resolves Augmented Stone Flicking CSS custom properties to concrete color
 * strings the Canvas 2D API can consume. The canvas API only accepts literal
 * color values, so we read computed styles once per render pass.
 */

export interface AsfCanvasTokens {
  readonly boardFill: string;
  readonly boardStroke: string;
  readonly sectorDivider: string;
  readonly vignette: string;
  readonly zoneP1: string;
  readonly zoneP2: string;
  readonly zoneP3: string;
  readonly zoneP4: string;
  readonly stoneP1: string;
  readonly stoneP1Bg: string;
  readonly stoneP2: string;
  readonly stoneP2Bg: string;
  readonly stoneP3: string;
  readonly stoneP3Bg: string;
  readonly stoneP4: string;
  readonly stoneP4Bg: string;
  readonly stoneSelectedGlow: string;
  readonly ready: string;
  readonly aimLine: string;
  readonly aimDot: string;
  readonly aimWeak: string;
  readonly aimMid: string;
  readonly aimStrong: string;
  readonly onAccent: string;
}

const TOKEN_NAMES = {
  boardFill: "--cn-asf-board-fill",
  boardStroke: "--cn-asf-board-stroke",
  sectorDivider: "--cn-asf-sector-divider",
  vignette: "--cn-asf-vignette",
  zoneP1: "--cn-asf-zone-p1",
  zoneP2: "--cn-asf-zone-p2",
  zoneP3: "--cn-asf-zone-p3",
  zoneP4: "--cn-asf-zone-p4",
  stoneP1: "--cn-asf-stone-p1",
  stoneP1Bg: "--cn-asf-stone-p1-bg",
  stoneP2: "--cn-asf-stone-p2",
  stoneP2Bg: "--cn-asf-stone-p2-bg",
  stoneP3: "--cn-asf-stone-p3",
  stoneP3Bg: "--cn-asf-stone-p3-bg",
  stoneP4: "--cn-asf-stone-p4",
  stoneP4Bg: "--cn-asf-stone-p4-bg",
  stoneSelectedGlow: "--cn-asf-stone-selected-glow",
  ready: "--cn-asf-ready",
  aimLine: "--cn-asf-aim-line",
  aimDot: "--cn-asf-aim-dot",
  aimWeak: "--cn-asf-aim-weak",
  aimMid: "--cn-asf-aim-mid",
  aimStrong: "--cn-asf-aim-strong",
  onAccent: "--cn-asf-on-accent",
} as const;

const FALLBACK: AsfCanvasTokens = {
  boardFill: "#d4c4a0",
  boardStroke: "#6b5b3e",
  sectorDivider: "rgba(107, 91, 62, 0.3)",
  vignette: "rgba(107, 91, 62, 0.45)",
  zoneP1: "rgba(26, 107, 62, 0.08)",
  zoneP2: "rgba(107, 26, 62, 0.08)",
  zoneP3: "rgba(26, 62, 107, 0.08)",
  zoneP4: "rgba(107, 91, 26, 0.08)",
  stoneP1: "#1a6b3e",
  stoneP1Bg: "#d5e8dc",
  stoneP2: "#6b1a3e",
  stoneP2Bg: "#e8d5dc",
  stoneP3: "#1a3e6b",
  stoneP3Bg: "#d5dce8",
  stoneP4: "#6b5b1a",
  stoneP4Bg: "#e8e4d5",
  stoneSelectedGlow: "#ffd700",
  ready: "#22c55e",
  aimLine: "rgba(200, 80, 20, 0.8)",
  aimDot: "rgba(200, 80, 20, 0.85)",
  aimWeak: "#22c55e",
  aimMid: "#f59e0b",
  aimStrong: "#ef4444",
  onAccent: "#ffffff",
} as const;

export function resolveAsfCanvasTokens(
  element: Element | null,
): AsfCanvasTokens {
  if (!element || typeof window === "undefined") return FALLBACK;
  const styles = window.getComputedStyle(element);
  const read = (name: string, fallback: string): string => {
    const value = styles.getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
  };
  return {
    boardFill: read(TOKEN_NAMES.boardFill, FALLBACK.boardFill),
    boardStroke: read(TOKEN_NAMES.boardStroke, FALLBACK.boardStroke),
    sectorDivider: read(TOKEN_NAMES.sectorDivider, FALLBACK.sectorDivider),
    vignette: read(TOKEN_NAMES.vignette, FALLBACK.vignette),
    zoneP1: read(TOKEN_NAMES.zoneP1, FALLBACK.zoneP1),
    zoneP2: read(TOKEN_NAMES.zoneP2, FALLBACK.zoneP2),
    zoneP3: read(TOKEN_NAMES.zoneP3, FALLBACK.zoneP3),
    zoneP4: read(TOKEN_NAMES.zoneP4, FALLBACK.zoneP4),
    stoneP1: read(TOKEN_NAMES.stoneP1, FALLBACK.stoneP1),
    stoneP1Bg: read(TOKEN_NAMES.stoneP1Bg, FALLBACK.stoneP1Bg),
    stoneP2: read(TOKEN_NAMES.stoneP2, FALLBACK.stoneP2),
    stoneP2Bg: read(TOKEN_NAMES.stoneP2Bg, FALLBACK.stoneP2Bg),
    stoneP3: read(TOKEN_NAMES.stoneP3, FALLBACK.stoneP3),
    stoneP3Bg: read(TOKEN_NAMES.stoneP3Bg, FALLBACK.stoneP3Bg),
    stoneP4: read(TOKEN_NAMES.stoneP4, FALLBACK.stoneP4),
    stoneP4Bg: read(TOKEN_NAMES.stoneP4Bg, FALLBACK.stoneP4Bg),
    stoneSelectedGlow: read(
      TOKEN_NAMES.stoneSelectedGlow,
      FALLBACK.stoneSelectedGlow,
    ),
    ready: read(TOKEN_NAMES.ready, FALLBACK.ready),
    aimLine: read(TOKEN_NAMES.aimLine, FALLBACK.aimLine),
    aimDot: read(TOKEN_NAMES.aimDot, FALLBACK.aimDot),
    aimWeak: read(TOKEN_NAMES.aimWeak, FALLBACK.aimWeak),
    aimMid: read(TOKEN_NAMES.aimMid, FALLBACK.aimMid),
    aimStrong: read(TOKEN_NAMES.aimStrong, FALLBACK.aimStrong),
    onAccent: read(TOKEN_NAMES.onAccent, FALLBACK.onAccent),
  };
}

export function getStonePalette(
  tokens: AsfCanvasTokens,
  ownerIndex: number,
): { readonly fill: string; readonly bg: string } {
  // Guard against negative ownerIndex (JS modulo returns negative for negative inputs).
  const seat = ((ownerIndex % 4) + 4) % 4;
  switch (seat) {
    case 0:
      return { fill: tokens.stoneP1, bg: tokens.stoneP1Bg };
    case 1:
      return { fill: tokens.stoneP2, bg: tokens.stoneP2Bg };
    case 2:
      return { fill: tokens.stoneP3, bg: tokens.stoneP3Bg };
    default:
      return { fill: tokens.stoneP4, bg: tokens.stoneP4Bg };
  }
}

export function getZoneTint(
  tokens: AsfCanvasTokens,
  ownerIndex: number,
): string {
  const seat = ((ownerIndex % 4) + 4) % 4;
  switch (seat) {
    case 0:
      return tokens.zoneP1;
    case 1:
      return tokens.zoneP2;
    case 2:
      return tokens.zoneP3;
    default:
      return tokens.zoneP4;
  }
}
